/**
 * FullTextMode Component
 * Audio player with synchronized text highlighting for full-text learning mode
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { segmentService } from '../../services/segmentService';
import type { Segment } from '../../types/segments';
import { useLearningStore } from '../../stores/learningStore';
import { useAuth } from '../../contexts/AuthContext';
import { getSegmentTranslation } from '../../utils/getTranslation';
import './FullTextMode.css';

interface FullTextModeProps {
    episodeId: string;
    audioUrl: string;
    courseId: string;
}

const FullTextMode: React.FC<FullTextModeProps> = ({ episodeId, audioUrl, courseId }) => {
    const { t } = useTranslation(['learning', 'common']);
    const { user } = useAuth();
    const userLang = user?.languagePreference || 'zh-TW';
    // State
    const [segments, setSegments] = useState<Segment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentSegmentIndex, setCurrentSegmentIndex] = useState<number>(-1);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [playbackRate, setPlaybackRate] = useState(1);

    // Refs
    const audioRef = useRef<HTMLAudioElement>(null);
    const segmentRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

    // Store actions
    const { updateProgress } = useLearningStore();

    // Fetch segments on mount
    useEffect(() => {
        const fetchSegments = async () => {
            try {
                setLoading(true);
                const data = await segmentService.getSegmentsByEpisode(episodeId);
                setSegments(data);
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err.message : t('common:failedToLoad'));
            } finally {
                setLoading(false);
            }
        };

        fetchSegments();
    }, [episodeId, t]);

    // Update current segment based on audio time
    useEffect(() => {
        if (segments.length === 0) return;

        const index = segments.findIndex(
            (seg) => currentTime >= seg.startTime && currentTime < seg.endTime
        );

        if (index !== currentSegmentIndex) {
            setCurrentSegmentIndex(index);

            // Auto-scroll to current segment
            if (index >= 0 && segmentRefs.current[index]) {
                segmentRefs.current[index]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        }
    }, [currentTime, segments, currentSegmentIndex]);

    // Audio event handlers
    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    const handleEnded = async () => {
        setIsPlaying(false);

        // Mark full text as completed
        try {
            await updateProgress(courseId, {
                mode: 'full_text',
                data: {
                    fullTextCompleted: true,
                    watchTimeSeconds: Math.floor(duration),
                },
            });
        } catch (err) {
            console.error('Failed to update progress:', err);
        }
    };

    // Control functions
    const togglePlayPause = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
        }
    };

    const handleSeek = (time: number) => {
        if (audioRef.current) {
            audioRef.current.currentTime = time;
            setCurrentTime(time);
        }
    };

    const handleVolumeChange = (newVolume: number) => {
        if (audioRef.current) {
            audioRef.current.volume = newVolume;
            setVolume(newVolume);
        }
    };

    const handlePlaybackRateChange = (rate: number) => {
        if (audioRef.current) {
            audioRef.current.playbackRate = rate;
            setPlaybackRate(rate);
        }
    };

    const handleSegmentClick = (segment: Segment) => {
        handleSeek(segment.startTime);
    };

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <div className="full-text-mode loading">
                <div className="spinner"></div>
                <p>{t('learning:fullTextMode.loading')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="full-text-mode error">
                <p className="error-message">{error}</p>
                <button onClick={() => window.location.reload()}>{t('common:retry')}</button>
            </div>
        );
    }

    return (
        <div className="full-text-mode">
            {/* Audio Element (hidden) */}
            <audio
                ref={audioRef}
                src={audioUrl}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnded}
            />

            {/* Audio Controls */}
            <div className="audio-controls">
                <button
                    className="play-pause-btn"
                    onClick={togglePlayPause}
                    aria-label={isPlaying ? t('common:pause') : t('common:play')}
                >
                    {isPlaying ? '⏸' : '▶️'}
                </button>

                <div className="time-display">
                    {formatTime(currentTime)} / {formatTime(duration)}
                </div>

                <div className="progress-bar-container">
                    <input
                        type="range"
                        className="progress-bar"
                        min={0}
                        max={duration || 0}
                        value={currentTime}
                        onChange={(e) => handleSeek(Number(e.target.value))}
                    />
                </div>

                <div className="volume-control">
                    <span>🔊</span>
                    <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.1}
                        value={volume}
                        onChange={(e) => handleVolumeChange(Number(e.target.value))}
                    />
                </div>

                <div className="playback-rate-control">
                    <select
                        value={playbackRate}
                        onChange={(e) => handlePlaybackRateChange(Number(e.target.value))}
                    >
                        <option value={0.5}>0.5x</option>
                        <option value={0.75}>0.75x</option>
                        <option value={1}>1x</option>
                        <option value={1.25}>1.25x</option>
                        <option value={1.5}>1.5x</option>
                        <option value={2}>2x</option>
                    </select>
                </div>
            </div>

            {/* Segments Display */}
            <div className="segments-container">
                {segments.map((segment, index) => (
                    <div
                        key={segment.id}
                        ref={(el) => (segmentRefs.current[index] = el)}
                        className={`segment ${
                            index === currentSegmentIndex ? 'active' : ''
                        } ${segment.speaker.toLowerCase()}`}
                        onClick={() => handleSegmentClick(segment)}
                    >
                        <div className="segment-header">
                            <span className="speaker-name">{segment.speaker}</span>
                            <span className="segment-time">
                                {formatTime(segment.startTime)}
                            </span>
                        </div>
                        <div className="segment-text">{segment.text}</div>
                        <div className="segment-translation">{getSegmentTranslation(segment, userLang)}</div>
                    </div>
                ))}
            </div>

            {/* Progress Info */}
            <div className="progress-info">
                <p>
                    {t('learning:fullTextMode.segmentProgress', { current: currentSegmentIndex + 1, total: segments.length })}
                </p>
            </div>
        </div>
    );
};

export default FullTextMode;
